/**
 * SF-5080: ask Eva how the waiting suggestions group. Each proposal can become one new item
 * (the item form, filled in and editable, saved through combine) or, when Eva named an existing
 * item, be folded into it. Nothing is saved until a manager acts on a proposal. Eva can take
 * up to two minutes, so the ask shows a spinner and can be cancelled (the answer is then ignored).
 *
 * sf-api groups exactly this list's waiting suggestions, the ones `suggestions` holds (the same
 * GET /items?status=suggested read). A proposal is always acted on whole: one naming a
 * suggestion not shown here (decided since, or sent after the read) says so, refreshes the
 * suggestions and cannot be applied, rather than quietly making an item from fewer of them.
 */
import { useMemo, useRef, useState } from 'react';
import { useCustomMutation } from '@refinedev/core';
import { Alert, App, Button, Card, Space, Spin, Typography } from 'antd';
import {
  combineSuggestionsArgs,
  droppedCompaniesMessage,
  droppedCompanies,
  type DroppedCompany,
  evaGroupingsArgs,
  mergeSuggestionArgs,
  prioritiesErrorCode,
  prioritiesErrorMessage,
  usePrioritiesWrite,
  type EvaGroup,
  type ItemInput,
  type PriorityCompany,
  type PriorityItem,
  type PriorityList,
} from '../api/prioritiesClient';
import { ItemFormModal } from './ItemFormModal';

interface Props {
  listId: string;
  lists: PriorityList[];
  suggestions: PriorityItem[];
  openItems: PriorityItem[];
  /** False while the waiting suggestions shown may be out of date (a refresh failed). */
  canDecide?: boolean;
  /** Reads the waiting suggestions again. */
  onRefresh: () => unknown;
}

/** Every company of the given sets once (by id), in the order first seen. */
const unionCompanies = (sets: PriorityCompany[][]): PriorityCompany[] => {
  const seen = new Map<string, PriorityCompany>();
  for (const c of sets.flat()) if (!seen.has(c.id)) seen.set(c.id, c);
  return [...seen.values()];
};

export const EvaGroupingsPanel: React.FC<Props> = ({
  listId,
  lists,
  suggestions,
  openItems,
  canDecide = true,
  onRefresh,
}) => {
  const { message } = App.useApp();
  const ask = useCustomMutation<{ groups: EvaGroup[] }>();
  const write = usePrioritiesWrite();
  const [groups, setGroups] = useState<EvaGroup[] | null>(null);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<EvaGroup | null>(null);
  const [startCompanies, setStartCompanies] = useState<PriorityCompany[]>([]);
  // Each ask gets a number; a cancelled or older ask's answer is dropped when it lands.
  const askNumber = useRef(0);
  const abort = useRef<AbortController | null>(null);

  const waiting = new Map(suggestions.map((s) => [s.id, s]));
  // How many of a proposal's suggestions are not among the waiting ones shown here.
  const missingCount = (g: EvaGroup) => g.suggestionIds.filter((id) => !waiting.has(id)).length;
  // A proposal is dropped from the panel once it has been applied.
  const done = (group: EvaGroup) => setGroups((gs) => gs && gs.filter((g) => g !== group));
  const listName = (id: string | null) => lists.find((l) => l.id === (id ?? listId))?.name;
  const itemTitle = (id: string) =>
    openItems.find((i) => i.id === id)?.title ?? 'the item Eva named';

  // Applied only whole, and only while the suggestions shown are known to be current; this also
  // turns off a Create item window already open when a refresh fails.
  const canApply = (g: EvaGroup | null) => !!g && canDecide && missingCount(g) === 0;

  // Kept stable while the window is open: a new object would refill the form on every render.
  const prefill = useMemo(
    () =>
      creating
        ? {
            title: creating.title,
            description: creating.description,
            listId: creating.listId,
            companies: startCompanies,
          }
        : undefined,
    [creating, startCompanies],
  );
  // sf-api takes combine's companyIds as the wanted set, so the form starts with every company
  // of the proposal's suggestions, read once on opening (a later refresh must not refill the
  // form); what the manager then removes, even all of them, is sent as it is.
  const openCreate = (group: EvaGroup) => {
    setStartCompanies(
      unionCompanies(group.suggestionIds.map((id) => waiting.get(id)?.companies ?? [])),
    );
    setCreating(group);
  };

  const run = async () => {
    const mine = ++askNumber.current;
    abort.current = new AbortController();
    setAsking(true);
    setError(null);
    try {
      const result = await ask.mutateAsync(evaGroupingsArgs(listId, abort.current.signal));
      if (mine === askNumber.current) {
        setGroups(result.data.groups);
        // A suggestion sent after this panel's read can be in a proposal: read them again.
        if (result.data.groups.some((g) => missingCount(g) > 0)) void onRefresh();
      }
    } catch (e) {
      if (mine === askNumber.current)
        setError(prioritiesErrorMessage(e, 'Eva could not group the suggestions.'));
    } finally {
      if (mine === askNumber.current) setAsking(false);
    }
  };
  const cancel = () => {
    askNumber.current += 1;
    abort.current?.abort();
    setAsking(false);
  };

  const create = async (values: ItemInput) => {
    if (!creating || !canApply(creating)) return;
    try {
      // Every suggestion of the proposal: combine makes the item from all of them or none.
      const result = await write.run(combineSuggestionsArgs(creating.suggestionIds, values));
      done(creating);
      void message.success('New item made from the suggestions');
      const left = droppedCompaniesMessage(droppedCompanies(result));
      if (left) void message.info(left);
    } catch (e) {
      // Someone decided one of them meanwhile: nothing changed, so show what is waiting now.
      if (prioritiesErrorCode(e) === 'SUGGESTION_ALREADY_DECIDED') void onRefresh();
      throw new Error(prioritiesErrorMessage(e, 'The item could not be made.'));
    }
  };

  const foldAll = async (group: EvaGroup) => {
    if (!group.existingItemId || !canApply(group)) return;
    // Companies left off by any of the merges, said once, even when a later merge fails.
    const dropped: DroppedCompany[] = [];
    try {
      // One call per suggestion: merge takes one suggestion at a time.
      for (const id of group.suggestionIds) {
        dropped.push(
          ...droppedCompanies(await write.run(mergeSuggestionArgs(id, group.existingItemId))),
        );
      }
      done(group);
      void message.success('Suggestions folded in');
    } catch (e) {
      // Any folded in before the failure now show as no longer waiting on the proposal.
      void onRefresh();
      void message.error(prioritiesErrorMessage(e, 'The suggestions could not be folded in.'));
    }
    const left = droppedCompaniesMessage(dropped);
    if (left) void message.info(left);
  };

  return (
    <Card
      size="small"
      title="Eva's groupings"
      extra={
        asking ? (
          <Button onClick={cancel}>Cancel</Button>
        ) : (
          <Button onClick={run} disabled={suggestions.length === 0}>
            Ask Eva to group suggestions
          </Button>
        )
      }
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {asking && (
          <Space>
            <Spin size="small" />
            <Typography.Text type="secondary">
              Eva is reading the suggestions. This can take up to two minutes.
            </Typography.Text>
          </Space>
        )}
        {error && <Alert type="error" showIcon message={error} />}
        {!asking && groups && groups.length === 0 && (
          <Typography.Text type="secondary">No proposals left.</Typography.Text>
        )}
        {!groups && !asking && !error && (
          <Typography.Text type="secondary">
            Eva proposes items from the waiting suggestions. Nothing is saved until you act.
          </Typography.Text>
        )}
        {(groups ?? []).map((group, index) => {
          const missing = missingCount(group);
          const blocked = !canApply(group);
          return (
            <Card
              key={`${index}:${group.suggestionIds.join(',')}`}
              size="small"
              title={group.title}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {missing > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message={`${missing} of these suggestions ${missing === 1 ? 'is' : 'are'} no longer waiting, so this proposal cannot be used as it is. Ask Eva again.`}
                  />
                )}
                {group.description && <Typography.Text>{group.description}</Typography.Text>}
                <Typography.Text type="secondary">
                  List: {listName(group.listId) ?? 'Not sure'}
                </Typography.Text>
                <ul aria-label={`Suggestions in ${group.title}`} style={{ margin: 0 }}>
                  {group.suggestionIds.map((id) => (
                    <li key={id}>{waiting.get(id)?.title ?? 'A suggestion no longer waiting'}</li>
                  ))}
                </ul>
                <Typography.Text type="secondary">Why: {group.reason}</Typography.Text>
                <Space>
                  <Button
                    type="primary"
                    size="small"
                    disabled={blocked}
                    onClick={() => openCreate(group)}
                  >
                    Create item
                  </Button>
                  {group.existingItemId && (
                    <Button
                      size="small"
                      disabled={blocked}
                      onClick={() => foldAll(group)}
                      loading={write.isLoading}
                    >
                      Fold into {itemTitle(group.existingItemId)}
                    </Button>
                  )}
                </Space>
              </Space>
            </Card>
          );
        })}
      </Space>
      <ItemFormModal
        open={!!creating}
        item={null}
        listId={listId}
        lists={lists}
        prefill={prefill}
        heading="Create item from suggestions"
        okText="Create item"
        canSubmit={canApply(creating)}
        extra={
          creating && (
            <>
              {!canApply(creating) && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="The waiting suggestions could not be refreshed, or some of these are no longer waiting, so this item cannot be made right now."
                />
              )}
              <Alert
                type="info"
                style={{ marginBottom: 12 }}
                message="This item takes in these suggestions"
                description={
                  <ul style={{ margin: 0 }}>
                    {creating.suggestionIds.map((id) => (
                      <li key={id}>{waiting.get(id)?.title}</li>
                    ))}
                  </ul>
                }
              />
            </>
          )
        }
        onSubmit={create}
        onClose={() => setCreating(null)}
      />
    </Card>
  );
};
